import { WalletService } from "@/app/core/apis/wallet.service";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, of, switchMap } from "rxjs";
import {
  loadBeneficiary,
  loadBeneficiaryFailure,
  loadBeneficiarySuccess,
  loadPaymentPayouts,
  loadPaymentPayoutsFailure,
  loadPaymentPayoutsSuccess,
  loadPaymentWallets,
  loadPaymentWalletsFailure,
  loadPaymentWalletsSuccess,
  loadPayoutParam,
  loadWalletOverview,
  loadWalletOverviewFailure,
  loadWalletOverviewSuccess,
  loadWalletTransactions,
  loadWalletTransactionsFailure,
  loadWalletTransactionsSuccess,
  loadedPayoutParamFailure,
  loadedPayoutParamSuccess,
  validatePayoutBeneficiary,
  validatePayoutBeneficiaryFailure,
  validatePayoutBeneficiarySuccess,
} from "./wallets.actions";
import { parseValidatePayoutBeneficiaryHttpError } from "./validate-payout-beneficiary-error.util";
@Injectable()
export class WalletsEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly walletService: WalletService
  ) {}

  loadWalletOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadWalletOverview),
      switchMap(({ businessId }) =>
        this.walletService.getWalletOverview(businessId).pipe(
          map((walletOverview) => loadWalletOverviewSuccess({ walletOverview })),
          catchError((error) => of(loadWalletOverviewFailure({ error: error?.error?.message })))
        )
      )
    )
  );

  loadPaymentWallets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPaymentWallets),
      switchMap(({ businessId }) =>
        this.walletService.getPaymentWallets(businessId).pipe(
          map((paymentWallets) => loadPaymentWalletsSuccess({ paymentWallets })),
          catchError((error) => of(loadPaymentWalletsFailure({ error: error?.error?.message })))
        )
      )
    )
  );

  loadWalletTransactions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadWalletTransactions),
      switchMap(({ businessId, page, limit, transactionType, source }) =>
        this.walletService.getWalletTransactions(businessId, page, limit, transactionType, source).pipe(
          map((walletTransactions) => loadWalletTransactionsSuccess({ walletTransactions })),
          catchError((error) => of(loadWalletTransactionsFailure({ error: error?.error?.message })))
        )
      )
    )
  );

  loadBeneficiary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBeneficiary),
      switchMap(({ businessId }) =>
        this.walletService.getBeneficiary(businessId).pipe(
          map((beneficiary) => loadBeneficiarySuccess({ beneficiary })),
          catchError((error) => of(loadBeneficiaryFailure({ error: error?.error?.message })))
        )
      )
    )
  );

  loadPaymentPayouts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPaymentPayouts),
      switchMap(({ businessId, page, limit }) =>
        this.walletService.getPayouts(businessId, page, limit).pipe(
          map((paymentPayouts) => loadPaymentPayoutsSuccess({ paymentPayouts })),
          catchError((error) => of(loadPaymentPayoutsFailure({ error: error?.error?.message })))
        )
      )
    )
  );

  loadPayoutParam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPayoutParam),
      switchMap(({ payoutMethod }) =>
        this.walletService.getPayoutParam(payoutMethod).pipe(
          map((data) => loadedPayoutParamSuccess({ data })),
          catchError((error) => of(loadedPayoutParamFailure({ error: error?.error?.message })))
        )
      )
    )
  );

  validatePayoutBeneficiary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(validatePayoutBeneficiary),
      switchMap(({ businessId }) =>
        this.walletService.validatePayoutBeneficiary(businessId).pipe(
          map((data) => validatePayoutBeneficiarySuccess({ data })),
          catchError((error) => {
            const { userMessage, failureKind } = parseValidatePayoutBeneficiaryHttpError(error);
            return of(validatePayoutBeneficiaryFailure({ error: userMessage, failureKind }));
          })
        )
      )
    )
  );
}
